import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Form } from "@/react/components/shared/uikit/Form";
import { ItemLabel } from "@/react/components/shared/uikit/Form/Item/ItemLabel";
import { localeRoot } from "../../../constants";
import { FieldContainer } from "../../components/FieldContainer";
import { ColorPickerField } from "../Fields/ColorPickerField";
import { FieldWithCounter } from "../Fields/FieldWithCounter";

interface BadgeFormProps {
  disabled: boolean;
  keyFormId: string;
  name: string;
}

const dataTestId = "paywall-customization-badge-fields";
const defaultFieldClassName = "!mt-0 !py-0";
const localeFields = "builder.badges.fields";
const translationRoot = [localeRoot, localeFields].join(".");

const generateNameFields = (name: string) => ({
  label: [name, "label"].join("."),
  text_color: [name, "text_color"].join("."),
  background_color: [name, "background_color"].join("."),
  icon: [name, "icon"].join("."),
});

const textColors = [
  "#000000",
  "#1F2937",
  "#374151",
  "#475569",
  "#1E3A8A",
  "#1E40AF",
  "#1E293B",
  "#064E3B",
  "#581C87",
  "#7C2D12",
  "#3C2415",
  "#451A03",
];
const backgroundColors = [
  "#F6F6F6",
  "#F5F2ED",
  "#F3F4F6",
  "#FEF7F7",
  "#F9EDE4",
  "#FDF1F1",
  "#FEF3E2",
  "#EBF4FF",
  "#ECFDF5",
  "#F3F0FF",
  "#E6FFFA",
  "#F1F0EE",
];

export const BadgeForm = ({ keyFormId, name }: BadgeFormProps) => {
  const fields = useMemo(() => generateNameFields(name), [name]);
  const { control } = useFormContext();

  return (
    <FieldContainer dataTestId={dataTestId}>
      <FieldWithCounter
        name={fields.label}
        label={t([translationRoot, "label"])}
        maxLength={30}
        errorMessage={t([translationRoot, "errors.label_max_length"])}
      >
        <Form.Item
          className={defaultFieldClassName}
          name={fields.label}
          translationRoot={translationRoot}
          hideLabel
          hidePlaceholder
          hideBorder
          hideDescription
          rules={{
            maxLength: {
              value: 30,
              message: t([translationRoot, "errors.label_max_length"]),
            },
          }}
          variant="extra-bottom-margin"
        >
          <Form.Input
            key={keyFormId}
            prefix={
              <Form.EmojiPickerV3Wrapper
                name={fields.icon}
                className="!w-[3rem]"
              />
            }
            data-testid="badge-label-input"
          />
        </Form.Item>
      </FieldWithCounter>
      <div className="flex flex-row items-center justify-between">
        <ItemLabel label={t([translationRoot, "text_color"])} />
        <Controller
          name={fields.text_color}
          control={control}
          render={({ field: { onChange, value } }) => (
            <ColorPickerField
              colors={textColors}
              subtitle={t([translationRoot, "text_color"])}
              onChange={onChange}
              value={value}
              dataTestId="badge-text-color-picker"
              pickerColorsTestId="badge-text-picker-colors"
            />
          )}
        />
      </div>
      <div className="flex flex-row items-center justify-between">
        <ItemLabel label={t([translationRoot, "background_color"])} />
        <Controller
          name={fields.background_color}
          control={control}
          render={({ field: { onChange, value } }) => (
            <ColorPickerField
              colors={backgroundColors}
              subtitle={t([translationRoot, "background_color"])}
              onChange={onChange}
              value={value}
              dataTestId="badge-background-color-picker"
              pickerColorsTestId="badge-background-picker-colors"
            />
          )}
        />
      </div>
    </FieldContainer>
  );
};
